import './design/tokens.css';
import './design/base.css';
import './design/components.css';
import './style.css';
import { formatApiStatus } from './lib/status';
import { probeWithRetry } from './lib/probe';
import { applyTheme, readStoredTheme } from './lib/theme';
import { keyAction, readSlide, slideLabel, stepIndex, type LightboxSlide } from './lib/lightbox';

// Honor a theme explicitly chosen elsewhere (e.g. the styleguide toggle).
applyTheme(readStoredTheme(localStorage), document.documentElement, localStorage);

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

async function init(): Promise<void> {
  const el = document.getElementById('api-status');
  if (!el) return;

  const result = await probeWithRetry(
    '/api/health',
    fetch.bind(window),
    sleep,
    6,
    5000,
    () => {
      el.textContent = 'warming up…';
      el.dataset.state = 'waiting';
    },
  );

  const label = formatApiStatus(result.httpStatus, result.body);
  el.textContent = label === 'ok' ? 'online' : label;
  el.dataset.state = label === 'ok' ? 'ok' : 'error';
}

void init();

/**
 * Gallery lightbox — a progressive enhancement.
 *
 * Each `.gallery-item` is a real <a> pointing at the largest available variant
 * of its photo, so with JS off (or broken) a click opens the full photo. That
 * is why these are links and not buttons: a button here would be a dead
 * control in exactly the situation you cannot see.
 *
 * The dialog is BUILT HERE rather than sitting in index.html, so pages without
 * a gallery carry no empty markup, and the large image is only requested when
 * someone actually opens one — which matters on a page with a known mobile LCP
 * problem. Nothing below runs at all if the page has no gallery.
 *
 * <dialog>.showModal() gives focus trapping, Escape-to-close, inertness of the
 * page behind, and a ::backdrop for free. Reimplementing any of that by hand is
 * how lightboxes end up inaccessible.
 */
function initLightbox(): void {
  const links = Array.from(document.querySelectorAll<HTMLAnchorElement>('a.gallery-item'));
  if (links.length === 0) return;

  const slides = links.map(readSlide).filter((s): s is LightboxSlide => s !== null);
  if (slides.length === 0) return;

  const dialog = document.createElement('dialog');
  dialog.className = 'lightbox';
  const figure = document.createElement('figure');
  const img = document.createElement('img');
  img.decoding = 'async';
  // No visible caption, by owner preference — the enlarged view is the photo
  // and nothing else. The accessibility story does not depend on it: the
  // description lives on the image's alt, and the counter is announced by the
  // hidden live region below. Removing the caption also retires the
  // description-twice problem it used to create with alt.
  const status = document.createElement('span');
  status.className = 'sr-only';
  status.setAttribute('aria-live', 'polite');
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'lightbox-close';
  close.setAttribute('aria-label', 'Close');
  close.textContent = '×';
  const prev = document.createElement('button');
  prev.type = 'button';
  prev.className = 'lightbox-nav';
  prev.dataset.dir = 'prev';
  prev.setAttribute('aria-label', 'Previous photo');
  prev.textContent = '‹';
  const next = document.createElement('button');
  next.type = 'button';
  next.className = 'lightbox-nav';
  next.dataset.dir = 'next';
  next.setAttribute('aria-label', 'Next photo');
  next.textContent = '›';

  figure.append(img, status);
  dialog.append(close, prev, figure, next);
  document.body.append(dialog);

  let index = 0;

  function show(next_: number): void {
    index = stepIndex(next_, 0, slides.length);
    const slide = slides[index];
    if (!slide) return;
    img.src = slide.href;
    // alt stays descriptive. An earlier version set it to '' to avoid the
    // figcaption repeating it — which is sanctioned by the spec, but a reviewer
    // pulled the real accessibility tree and showed the cost: alt="" marks the
    // image presentational, so the lightbox's ONLY content vanished from the
    // tree entirely. Image quick-nav (NVDA/JAWS "G", the VoiceOver rotor) found
    // nothing inside a dialog whose whole purpose is one photo. Mild
    // duplication on a linear browse is the smaller price.
    img.alt = slide.alt;
    // Set intrinsic size so the browser reserves the right box and the image
    // does not jump as it decodes.
    if (slide.width > 0) img.width = slide.width;
    if (slide.height > 0) img.height = slide.height;
    status.textContent = slideLabel(index, slides.length);
  }

  function move(delta: number): void {
    show(stepIndex(index, delta, slides.length));
  }

  links.forEach((link, i) => {
    link.addEventListener('click', (event) => {
      // Leave modified clicks alone — ctrl/cmd/shift/middle-click should still
      // open the photo in a new tab, which is what the href is for.
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      event.preventDefault();
      show(i);
      dialog.showModal();
    });
  });

  close.addEventListener('click', () => dialog.close());
  prev.addEventListener('click', () => move(-1));
  next.addEventListener('click', () => move(1));

  dialog.addEventListener('keydown', (event) => {
    const action = keyAction(event.key);
    if (!action) return;
    if (action === 'close') return; // <dialog> closes itself on Escape
    event.preventDefault();
    move(action === 'next' ? 1 : -1);
  });

  // Click the backdrop to dismiss. The dialog element fills the viewport, so
  // "outside the figure" is the test rather than "outside the dialog".
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
}

initLightbox();
